// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@conklin/App/Components/Cart/CartLineContainer";
import Notify from "@conklin/App/Components/Notify";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import Link, { LinkPresentationProps } from "@conklin/MobiusOverrides/Link/Link";
import Tooltip, { TooltipPresentationProps } from "@conklin/MobiusOverrides/Tooltip/Tooltip";
import { ProductAvailabilityStyles } from "@conklin/Overrides/Components/ProductAvailability";
import { ADD_TO_PROJECT } from "@conklin/XDL/constants";
import { parseBoolean } from "@conklin/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    // XNG-Changes
    showAddToList?: boolean;
    includeActions?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    // XNG-Changes
    wishListSettings: getSettingsCollection(state).wishListSettings,
    isAuthenticated: xIsAuthenticated(state),
});

// XNG-Changes
const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-Changes
    productAvailability?: ProductAvailabilityStyles;
    productInfoContainer?: InjectableCss;
    addToListLink?: LinkPresentationProps;
    cartLineContainer?: CartLineContainerStyles;
    quoteMessageWrapper?: InjectableCss;
    quoteTotal?: TypographyPresentationProps;
    quoteText?: TypographyPresentationProps;
    quoteTooltip?: TooltipPresentationProps;
    invalidtPriceHeadingAndText?: SmallHeadingAndTextStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // XNG-Changes
    showAddToList = true,
    includeActions = true,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    isAuthenticated,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    // <XNG-Changes>
    const toasterContext = useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };
    // </XNG-Changes>

    // XNG-Changes
    const quoteRequired =
        cartLine.quoteRequired ||
        (!isAuthenticated && parseBoolean(cartLine.properties.authRequiredForPurchase)) ||
        parseBoolean(cartLine.properties.isQuote);
    const showPriceForQuotes = parseBoolean(cartLine.properties.showPriceQuoteRequired ?? false); // XNG-Changes: use CartLineContainer

    return (
        <CartLineContainer
            extendedStyles={styles.cartLineContainer}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            includeActions={includeActions}
        >
            <CartLineContainer.Image {...styles.productImageGridItem} {...{ className: "hideForPrint" }}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </CartLineContainer.Image>
            <CartLineContainer.Description {...styles.orderLineInfoGridItem}>
                {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                <StyledWrapper {...styles.productInfoContainer}>
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        // XNG-Changes
                        showCustomerName={true}
                        showManufacturerItem={true}
                        {...{ sku: cartLine.properties?.sku || "" }}
                    />
                    {/* XNG-Changes: add !quoteRequired */}
                    {!cart.cartNotPriced && (!quoteRequired || showPriceForQuotes) && (
                        <ProductPrice
                            product={cartLine}
                            showSavings={true}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            extendedStyles={styles.price}
                            // XNG-Changes
                            label={translate("Price/Unit")}
                        />
                    )}
                    {/* XNG-Changes */}
                    {parseBoolean(cart.properties.erpSystemIsDown) && (
                        <Typography as="p" {...styles.price?.price?.errorText}>
                            {translate("Call For Pricing")}
                        </Typography>
                    )}
                    {promotions?.map(promotion => (
                        <Typography
                            {...styles.promotionNameText}
                            key={promotion.id}
                            data-test-selector="orderLineCardExpandedPromotionName"
                        >
                            {promotion.name}
                        </Typography>
                    ))}
                </StyledWrapper>
                {!cartLine.isFixedConfiguration &&
                    cartLine.sectionOptions!.length > 0 &&
                    cartLine.sectionOptions!.map(option => (
                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                            {`${option.sectionName}:${option.optionName}`}
                        </Typography>
                    ))}
            </CartLineContainer.Description>
            <CartLineContainer.Inventory>
                {/* XNG-Changes/ */}
                <ProductAvailability
                    productId={cartLine.productId!}
                    availability={cartLine.availability!}
                    unitOfMeasure={cartLine.unitOfMeasure}
                    trackInventory={cartLine.trackInventory}
                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                    extendedStyles={styles.productAvailability}
                />
            </CartLineContainer.Inventory>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    // XNG-Changes
                    editable={editable && !parseBoolean(cart.properties.erpSystemIsDown)}
                    extendedStyles={styles.quantity}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {/* XNG-Changes */}
                {(!quoteRequired || showPriceForQuotes) &&
                    cartLine.pricing &&
                    !parseBoolean(cart.properties.erpSystemIsDown) &&
                    (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <SmallHeadingAndText
                                        heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                        extendedStyles={styles.subtotalWithoutVatText}
                                    />
                                    <SmallHeadingAndText
                                        heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                        text={cartLine.pricing.vatAmountDisplay}
                                        extendedStyles={styles.vatAmount}
                                    />
                                </>
                            )}
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPrice}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={
                                    !enableVat
                                        ? translate("Total")
                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                        ? `${translate("Total")} (${translate("Inc. VAT")})`
                                        : `${translate("Total")} (${translate("Ex. VAT")})`
                                }
                                text={
                                    !cartLine.pricing.extendedUnitRegularPrice && !cartLine.pricing.extendedUnitNetPrice
                                        ? translate("Call For Pricing")
                                        : enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />
                        </StyledWrapper>
                    )}
                {/* XNG-Changes */}
                {parseBoolean(cart.properties.erpSystemIsDown) && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <SmallHeadingAndText
                            {...styles.extendedUnitNetPrice}
                            data-test-selector="cartline_extendedUnitNetPrice"
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Total")} (${translate("Inc. VAT")})`
                                    : `${translate("Total")} (${translate("Ex. VAT")})`
                            }
                            text={translate("Call For Pricing")}
                        />
                    </StyledWrapper>
                )}
                {/* XNG-Changes */}
                {quoteRequired && !parseBoolean(cart.properties.erpSystemIsDown) && !showPriceForQuotes && (
                    <StyledWrapper {...styles.quoteMessageWrapper}>
                        <Typography {...styles.quoteTotal}>{translate("Total")}</Typography>
                        <Typography {...styles.quoteText} data-test-selector="quoteRequiredText">
                            {translate("Quote")}
                        </Typography>
                        <Tooltip {...styles.quoteTooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Notes>
                {cartLine.notes && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        label={translate("Line Notes")}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
                {cartLine.isRestricted && (
                    <Notify
                        {...styles.cartLineErrorMessageText}
                        type="danger"
                        showClose={false}
                        sizeVariant="small"
                        data-test-selector="cartline_restrictedMessage"
                    >
                        {translate("Restricted product")}
                    </Notify>
                )}
                {!cartLine.isActive && (
                    <Notify type="danger" sizeVariant="small" showClose={false} {...styles.cartLineErrorMessageText}>
                        {translate("Inactive Product")}
                    </Notify>
                )}
                {/* XNG-Changes */}
                {showAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                        {ADD_TO_PROJECT}
                    </Link>
                )}
            </CartLineContainer.Notes>
            <CartLineContainer.Actions>
                {cart.showCostCode && cartLine.costCode && (
                    <Typography {...styles.costCodeValueText} as="p">
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                        {cartLine.costCode}
                    </Typography>
                )}
            </CartLineContainer.Actions>
            <CartLineContainer.Remove></CartLineContainer.Remove>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
